import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { AddUserIcon } from "@/components/Icons/AddUserIcon";
import { AptihealthIcon } from "@/components/Icons/AptihealthIcon";
import { BellIcon } from "@/components/Icons/BellIcon";
import { CameraIcon } from "@/components/Icons/CameraIcon";
import { Card2 } from "@/ui2/atoms/Card";
import { CardIcon } from "@/components/Icons/CardIcon";
import { DocumentIcon } from "@/components/Icons/DocumentIcon";
import { EnvelopeIcon } from "@/components/Icons/EnvelopeIcon";
import { getPatientAge } from "@/lib/helpers/getpatientAge";
import { isAdult } from "@/utils/profileUtilsTyped";
import { LockIcon } from "@/components/Icons/LockIcon";
import { logOut } from "@/redux/actions/auth";
import Logout from "@/components/Common/SettingsView/Logout";
import { PaywallReminder } from "@/features/dashboard/components/reminder-banner/PaywallReminder";
import { ReminderContainer } from "@/features/dashboard/components/reminder-banner/ReminderContainer";
import { selectIsACPEligible } from "@/redux/selectors/patientSelectors";
import { selectProfile } from "@/redux/selectors/authSelectors";
import { selectShowPaywall } from "@/redux/selectors/patientCardInfoSelectors";
import { ShieldIcon } from "@/components/Icons/ShieldIcon";
import { TourIcon } from "@/components/Icons/TourIcon";
import { UserIcon } from "@/components/Icons/UserIcon";
import { ViewTitle } from "@/features/dashboard/components/ViewTitle";

import { Setting, SettingsCard } from "./components/Card";

export function PatientSettings({ baseRoute }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const isACPEligible = useSelector(selectIsACPEligible);
  const profile = useSelector(selectProfile);
  const isPaywallBannerShown = useSelector(selectShowPaywall);

  const isPatientAdult = isAdult(profile?.dob);
  const displayConsentsPage = isACPEligible && isPatientAdult;
  const showAddChild = profile?.dob != null && 18 < getPatientAge(profile.dob);

  return (
    <>
      <ReminderContainer>{isPaywallBannerShown && <PaywallReminder />}</ReminderContainer>

      <ViewTitle>Settings</ViewTitle>

      <SettingsCard title="Account">
        <Setting
          iconComponent={UserIcon}
          onClick={() => history.push(baseRoute + "/profile")}
          title="Profile"
        />

        <Setting
          iconComponent={LockIcon}
          onClick={() => history.push(baseRoute + "/change-password")}
          title="Password"
        />

        <Setting
          iconComponent={ShieldIcon}
          onClick={() => history.push(baseRoute + "/patient-insurance")}
          title="Insurance"
        />

        <Setting
          iconComponent={CardIcon}
          onClick={() => history.push(baseRoute + "/patient-payment")}
          title="Payment"
        />

        {showAddChild && (
          <Setting
            iconComponent={AddUserIcon}
            onClick={() => history.push(baseRoute + "/teens-and-kids/program-details")}
            title="Add Child"
          />
        )}

        <Setting
          iconComponent={BellIcon}
          noBorderBottom
          onClick={() => history.push(baseRoute + "/patient-notifications")}
          title="Notifications"
        />
      </SettingsCard>

      <SettingsCard title="Support">
        <Setting
          iconComponent={AptihealthIcon}
          onClick={() => window.open("https://www.aptihealth.com/about/", "_blank").focus()}
          title="About aptihealth"
        />

        <Setting
          iconComponent={EnvelopeIcon}
          onClick={() =>
            window.open("mailto:support@aptihealth.com?subject=Web Support Request", "_top").focus()
          }
          title="Member Support"
        />

        <Setting
          iconComponent={TourIcon}
          onClick={() => history.push(baseRoute + "/tutorial")}
          title="Tutorials"
        />

        <Setting
          iconComponent={CameraIcon}
          noBorderBottom
          onClick={() => history.push(baseRoute + "/av-check")}
          title="Audio & Visual Check"
        />
      </SettingsCard>

      <SettingsCard title="Legal">
        <Setting
          iconComponent={DocumentIcon}
          noBorderBottom={!displayConsentsPage}
          onClick={() => window.open("https://www.aptihealth.com/hipaaprivacy", "_blank").focus()}
          title="HIPAA Notice of Privacy Practices"
        />

        {displayConsentsPage && (
          <Setting
            iconComponent={DocumentIcon}
            noBorderBottom
            onClick={() => history.push(baseRoute + "/consents")}
            title="Consents"
          />
        )}
      </SettingsCard>

      <Card2 className="tw:flex-col">
        <Logout onLogOut={() => dispatch(logOut({ history }))} />
      </Card2>
    </>
  );
}
